/**
 * Toast policy for inbound live messages.
 *
 * The repo uses react-hot-toast with no ids or dedupe anywhere, which is fine
 * for one-off confirmations and disastrous for a message stream — a busy
 * afternoon would stack dozens. Everything here exists to keep that from
 * happening.
 */

import toast from "react-hot-toast";
import { playPing } from "./notificationSound";

/** One toast per session at a time; a newer message replaces, never stacks. */
const toastId = (sessionId) => `live-${sessionId}`;
const PER_SESSION_THROTTLE_MS = 8_000;
const MAX_CONCURRENT = 3;
/** Cross-tab claim, so three open dashboards produce one toast, not three. */
const CLAIM_PREFIX = "liveagent:toast:";
const CLAIM_TTL_MS = 60_000;
const READ_PREFIX = "liveagent:read:";

const lastToastAt = new Map();
const active = new Set();

export const isMuted = () => localStorage.getItem("liveagent:muted") === "1";
export const setMuted = (muted) => {
  localStorage.setItem("liveagent:muted", muted ? "1" : "0");
};

/**
 * Returns false if another tab already claimed this turn. Hidden tabs pause
 * briefly first so a visible one wins the race.
 */
function claimTurn(turnId) {
  if (!turnId) return true;
  const key = CLAIM_PREFIX + turnId;
  try {
    const existing = localStorage.getItem(key);
    if (existing && Date.now() - Number(existing) < CLAIM_TTL_MS) return false;
    localStorage.setItem(key, String(Date.now()));
    sweepClaims();
    return true;
  } catch {
    return true;
  }
}

function sweepClaims() {
  const cutoff = Date.now() - CLAIM_TTL_MS;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(CLAIM_PREFIX)) continue;
    if (Number(localStorage.getItem(key)) < cutoff)
      localStorage.removeItem(key);
  }
}

/**
 * "Aurora Heights · Unit 412" — whatever the session snapshot actually has.
 * Field names aren't nailed down across backend versions, so this checks a
 * few plausible ones and quietly omits what isn't there rather than showing
 * a raw id or an empty dash.
 */
export function buildContactMeta(session) {
  if (!session) return null;
  const place =
    session.property_name ||
    session.website_name ||
    session.property_label ||
    session.website_label ||
    null;
  const unit = session.unit_number || session.unit_name || session.unit_label;
  const parts = [place, unit ? `Unit ${unit}` : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * The toast is deliberately narrow — pinging on every AI-handled exchange
 * would bury an agent in noise. It fires for exactly two things: (1) a
 * customer asking to talk to a person (intent === "contact", the same
 * bucket the "Contact Requests" tile counts) that nobody has picked up yet —
 * once an agent claims it, this alert has done its job; and (2) a message in
 * a conversation THIS agent has already taken over, where the AI won't
 * answer and the customer is waiting on them specifically. An AI-handled
 * conversation with no contact request, or one another agent already owns,
 * stays quiet.
 */
export function shouldAlert(session, currentUserId) {
  if (!session) return false;
  if (session.intent === "contact" && session.handling_agent_id == null) {
    return true;
  }
  if (currentUserId == null || session.handling_agent_id == null) return false;
  return String(session.handling_agent_id) === String(currentUserId);
}

export function notifyIncoming({
  sessionId,
  turnId,
  name,
  meta,
  text,
  onOpen,
}) {
  if (isMuted()) return;

  const last = lastToastAt.get(sessionId) ?? 0;
  if (Date.now() - last < PER_SESSION_THROTTLE_MS) return;

  const fire = () => {
    if (!claimTurn(turnId)) return;
    if (active.size >= MAX_CONCURRENT) {
      const oldest = active.values().next().value;
      toast.dismiss(oldest);
      active.delete(oldest);
    }

    const id = toastId(sessionId);
    lastToastAt.set(sessionId, Date.now());
    active.add(id);
    playPing();

    toast(
      (t) => (
        <button
          type="button"
          className="flex items-start gap-2 text-left max-w-[240px]"
          onClick={() => {
            toast.dismiss(t.id);
            active.delete(id);
            onOpen?.();
          }}
        >
          <span className="relative flex h-2 w-2 mt-1 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          <span className="min-w-0">
            <span className="flex items-baseline gap-1 min-w-0">
              <span className="text-[13px] font-bold text-black truncate min-w-0">
                {name || "Anonymous visitor"}
              </span>
              {meta && (
                <span className="text-[10.5px] text-grey1 truncate flex-shrink min-w-0">
                  · {meta}
                </span>
              )}
            </span>
            <span className="block text-[12px] text-grey1 truncate">
              {String(text || "").slice(0, 48)}
              {String(text || "").length > 48 ? "…" : ""}
            </span>
          </span>
        </button>
      ),
      {
        id,
        duration: 5000,
        style: {
          padding: "11px 13px",
          borderLeft: "3px solid #0071BC",
          boxShadow: "0 12px 28px rgba(0,0,0,0.20), 0 3px 8px rgba(0,0,0,0.12)",
        },
      }
    );
  };

  if (typeof document !== "undefined" && document.hidden) {
    setTimeout(fire, 150);
  } else {
    fire();
  }
}

/** Tell sibling tabs a session has been read, so their badge clears too. */
export function broadcastRead(sessionId) {
  try {
    localStorage.setItem(READ_PREFIX + sessionId, String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function onCrossTabRead(handler) {
  const listener = (e) => {
    if (e.key?.startsWith(READ_PREFIX))
      handler(e.key.slice(READ_PREFIX.length));
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}
